import { IconCheckDefault } from '@iwoca/icons';
import { AlertBox, Text } from '@iwoca/orion';
import { NEW_IwLinkButton } from '@iwRouter/NEW_IwLinkButton';
import { FormattedMessage } from 'react-intl';

import { useGetOnboardingRequirements } from './useGetOnboardingRequirements';
import { deserialiseFundingRequirements } from '../../../funding/fundingSteps/deserialiseFundingRequirements';
import { TFundingRequirementsSteps } from '../../../funding/fundingSteps/orderFundingRequirements';
import { ONBOARDING_PATHS } from '../../constants';
import { AddCompanyShareholders } from '../addCompanyShareholders/AddCompanyShareholders';
import { AddPaymentMethod } from '../addPaymentMethod/AddPaymentMethod';
import { CompleteIDCheck } from '../idCheck/CompleteIDCheck';

const OnboardingStep = ({ step }: { step: TFundingRequirementsSteps[0] }) => {
    const type = step.type.toUpperCase();
    switch (type) {
        case 'DEBIT_CARD':
            return <AddPaymentMethod step={step} />;
        case 'ONFIDO_VIDEO_VERIFICATION':
        case 'ONFIDO_VERIFICATION':
        case 'IDVERSE_VERIFICATION':
            return <CompleteIDCheck step={step} />;
        case 'MAJOR_SHAREHOLDER_CHECK':
            return <AddCompanyShareholders step={step} />;
        default:
            return null;
    }
};

export const OnboardingChecks = () => {
    const { onboardingRequirements } = useGetOnboardingRequirements();

    const sortedOnboardingRequirements =
        //@ts-expect-error: Onboarding requirements response type difference
        deserialiseFundingRequirements(onboardingRequirements!).sort(
            (a, b) =>
                (a.status === 'REQUIRED' ? -1 : 0) -
                (b.status === 'REQUIRED' ? -1 : 0)
        );

    const onboardingRequirementsComplete = !onboardingRequirements.find(
        (requirement) =>
            requirement.category !== 'contractual' &&
            requirement.status !== 'completed'
    );

    return (
        <div>
            {onboardingRequirementsComplete && (
                <AlertBox className="my-3xl" variant="success">
                    <Text className="flex flex-row gap-m text-s font-bold">
                        <IconCheckDefault
                            fontSize={30}
                            className="fill-success-drk pb-m"
                        />
                        <FormattedMessage
                            id="_.credit_card.onboarding.checks.all_requirements_complete"
                            defaultMessage="You’ve completed all the required steps. If you’re happy that the information below is correct, click ‘Confirm and continue’."
                        />
                    </Text>
                </AlertBox>
            )}

            <Text className="mt-2xl">
                <h1>
                    <FormattedMessage
                        id="_.credit_card.onboarding.checks.title"
                        defaultMessage="Complete checks"
                    />
                </h1>
                <Text className="text-xl">
                    <FormattedMessage
                        id="_.credit_card.onboarding.checks.sub_title"
                        defaultMessage="You can complete the steps below in any order."
                    />
                </Text>
            </Text>
            <div className="mt-2xl border-t-[1px] border-t-secondary-90">
                {sortedOnboardingRequirements.map(
                    (step: TFundingRequirementsSteps[0], index: number) => (
                        <div
                            id={`funding-step-${index}-${step.type}`}
                            key={`funding-step-${index}-${step.type}`}
                        >
                            <OnboardingStep step={step} />
                        </div>
                    )
                )}
            </div>
            {onboardingRequirementsComplete && (
                <NEW_IwLinkButton
                    variant="primary"
                    to={{
                        pathname: ONBOARDING_PATHS.ONBOARDING_DOCUMENTS
                    }}
                    className="my-3xl"
                >
                    <Text className="text-m font-bold text-structure-100">
                        <FormattedMessage
                            id="_.credit_card.onboarding.documents.continue"
                            defaultMessage="Confirm and Continue"
                        />
                    </Text>
                </NEW_IwLinkButton>
            )}
        </div>
    );
};
